import React, { useState, useEffect } from 'react';
import { Row, Modal, Input, Typography, Card, Badge, Spin, message, Button, Avatar } from 'antd';
import { SearchOutlined, CheckCircleOutlined, LoadingOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { RootState } from 'store/index';
import { useAppSelector, useAppDispatch } from "common/hooks/redux";
import { toggleConnectWalletModal, toggleSupportedNetworkModal } from 'store/global/';
import { useAccount, useSwitchChain, useConnectorClient } from 'wagmi';
import { mainnet, sepolia, polygon, polygonAmoy, bsc, bscTestnet } from 'wagmi/chains';
import './NetworkModal.scss';
import poly from "@/assets/images/chainlogos/poly.png";
import binance from "@/assets/images/chainlogos/bn.png";
import ether from "@/assets/images/chainlogos/Ether.png";
import { ENABLED_NETWORKS, supportedChainIds } from "common/constants";

const { Title, Text } = Typography;

const NetworkModal: React.FC = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const supportedNetworkModal = useAppSelector((state: RootState) => state.global.supportedNetworkModal);

  const { chain, isConnected } = useAccount();
  const { switchChainAsync, chains } = useSwitchChain();
  const currentChainId = chain?.id;
  const { data: connectorClient } = useConnectorClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingNetwork, setConnectingNetwork] = useState<string | null>(null);
  const [currentNetwork, setCurrentNetwork] = useState<string | null>(null);


  const filteredNetworks = ENABLED_NETWORKS.filter((network) =>
    network.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  function toggleModal(visible: boolean = false) {
    dispatch(toggleSupportedNetworkModal(visible));
  }

  const getCurrentNetworkId = () => {
    if (!isConnected) return null;
    const network = ENABLED_NETWORKS.find((n) => parseInt(n.chainIdDecimal) === currentChainId);
    return network ? network.id : null;
  };

  const isUnsupportedNetwork =
    isConnected &&
    (currentChainId === undefined || !supportedChainIds.includes(currentChainId as any));

  useEffect(() => {
    if (supportedNetworkModal) {
      setCurrentNetwork(getCurrentNetworkId());
    }
  }, [supportedNetworkModal, currentChainId, isConnected]);

  useEffect(() => {
    if (!isConnected) {
      setCurrentNetwork(null);
    }
  }, [isConnected]);

  const connectToMetaMask = () => {
    dispatch(toggleConnectWalletModal(true));
    return;
  };

  const switchNetwork = async (network: any) => {
    if (currentChainId && currentChainId === Number(network?.chainIdDecimal)) {
      return;
    }

    setIsConnecting(true);
    setConnectingNetwork(network.id);

    try {
      message.loading(`Switching to ${network.name}...`, 0);

      const chainId = parseInt(network.chainIdDecimal);
      if (!chains.some((chain) => chain.id === chainId)) {
        throw new Error(`Chain ID ${chainId} is not supported.`);
      }

      await switchChainAsync({ chainId: chainId as 1 | 11155111 | 137 | 80002 | 56 | 97 });

      setCurrentNetwork(network.id);
      message.destroy();
      message.success(`Successfully switched to ${network.name}`);

      setTimeout(() => {
        toggleModal(false);
      }, 1000);
    } catch (error: any) {
      console.error('Error switching network:', error);

      if (error.code === 4902 && network.nativeCurrency) {
        try {
          await connectorClient?.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${currentChainId?.toString(16)}`,
                chainName: network.name,
                nativeCurrency: network.nativeCurrency,
                rpcUrls: [...network.rpcUrls],
                blockExplorerUrls: [...network.blockExplorerUrls],
              },
            ],
          });

          await switchChainAsync({ chainId: currentChainId as 1 | 11155111 | 137 | 80002 | 56 | 97 });

          setCurrentNetwork(network.id);
          message.destroy();
          message.success(`Successfully added and switched to ${network.name}`);

          setTimeout(() => {
            toggleModal(false);
          }, 1000);
        } catch (addError) {
          console.error('Error adding network:', addError);
          message.destroy();
          message.error('Failed to add network. Please try again.');
        }
      } else {
        message.destroy();
        message.error('Failed to switch network. Please try again.');
      }
    } finally {
      setIsConnecting(false);
      setConnectingNetwork(null);
    }
  };

  return (
    <Modal
      open={supportedNetworkModal}
      title={
        <div className="network-title-container">
          <Title className="network-title" level={4} style={{ margin: 0 }}>
            Select Network
          </Title>
          <Text className="network-subtitle">
            Choose a network to connect your wallet
          </Text>
          <div className="network-search" style={{ marginTop: 16 }}>
            <Input
              placeholder="Search networks..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
            />
          </div>
        </div>
      }
      width="600px"
      wrapClassName="network-modal"
      onCancel={() => toggleModal(false)}
      footer={null}
    >
      <div className="nft-detail">
        <Row gutter={24}>
          {!isConnected && (
            <div style={{ width: '100%', textAlign: 'center', marginBottom: 16 }}>
              <Button type="primary" className="connect-wallet-btn" onClick={connectToMetaMask}>
                Connect Wallet
              </Button>
              <div className="network-warning">
                <Card>
                  <div className="warning-content">
                    <ExclamationCircleOutlined className="warning-icon" />
                    <div className="warning-text">
                      <Text className="warning-title">Wallet Integration</Text>
                      <Text className="warning-description">
                        This will switch your wallet to the selected network. Ensure your wallet is connected and unlocked.
                      </Text>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}
          {isConnected && isUnsupportedNetwork && (
            <div className="unsupported-network">
              <Card className="unsupported-card">
                <div className="unsupported-content">
                  <CloseCircleOutlined className="unsupported-icon" />
                  <div className="unsupported-text">
                    <Text className="unsupported-title">Wrong Network</Text>
                    <Text className="unsupported-suggestion">
                      Please switch to one of the supported networks below.
                    </Text>
                  </div>
                </div>
              </Card>
            </div>
          )}
          <div style={{ width: '100%' }}>
            <Row gutter={[0, 10]}>
              {filteredNetworks.map((network) => (
                <div key={network.id} style={{ width: '100%' }}>
                  <Card
                    className={`network-card ${currentNetwork === network.id && isConnected ? 'network-card--active' : ''
                      } ${isConnecting && connectingNetwork !== network.id
                        ? 'network-card--disabled'
                        : ''
                      }`}
                    onClick={() => !isConnecting && isConnected && switchNetwork(network)}
                  >
                    <div className="network-card-content">
                      <div className="network-info">
                        <div className={`network-icon ${network.color}`}>
                          <Avatar
                            src={network.icon}
                            size="small"
                            className="network"
                          />
                          {network.isTestnet && (
                            <div className="testnet-indicator">T</div>
                          )}
                        </div>
                        <div className="network-details">
                          <div className="network-name">
                            <Title level={5}>{network.name}</Title>
                            {network.isTestnet && (
                              <Badge count="Testnet" className="testnet-badge" />
                            )}
                          </div>
                          <Text className="network-meta">
                            {network.currency} • Chain ID: {network.chainIdDecimal}
                          </Text>
                        </div>
                      </div>

                      <div className="network-status">
                        {connectingNetwork === network.id && isConnecting && (
                          <Spin indicator={<LoadingOutlined spin />} />
                        )}
                        {currentNetwork === network.id && isConnected && !isConnecting && (
                          <div className="network-status-active">
                            <div className="status-dot" />
                            <Text className="status-text">Connected</Text>
                            <div className="network-check-icon">
                              <CheckCircleOutlined />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </Row>
          </div>

          {filteredNetworks.length === 0 && searchTerm && (
            <div className="network-no-results">
              <Text>No networks found matching "{searchTerm}"</Text>
            </div>
          )}
        </Row>
      </div>
    </Modal>
  );
};

export default NetworkModal;